import React, { useMemo, useState } from "react";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useDebounce from "react-use/lib/useDebounce";

import MediaAPI from "../../api/MediaAPI";
import Block from "../block/Block";
import { MultiComboboxWithAvatarBase } from "../form-fields/MultiComboboxWithAvatar";

import type { JSX } from "react";

import type { GetCastingListResponse } from "../../api/MediaAPI.types";
import type { SmartlistCasting } from "../../api/SmartlistAPI.types";
import type { AvatarComboboxOption } from "../form-fields/MultiComboboxWithAvatar";

const SEARCH_DEBOUNCE_MS = 400;

interface SmartlistCastingsBlockProps {
  value: SmartlistCasting[];
  onChange: (castings: SmartlistCasting[]) => void;
}

export default function SmartlistCastingsBlock({
  value,
  onChange,
}: SmartlistCastingsBlockProps): JSX.Element {
  const { t } = useTranslation("editor-smartlist");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useDebounce(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS, [search]);

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["getCastingList", "infinite", debouncedSearch],
      queryFn: async (queryParams) =>
        MediaAPI.getCastingList({
          page: queryParams.pageParam,
          perPage: 20,
          search: debouncedSearch,
        }),
      getNextPageParam: (lastPage: GetCastingListResponse) =>
        lastPage.page.number < lastPage.page.total
          ? lastPage.page.number + 1
          : undefined,
    });

  const searchOptions: AvatarComboboxOption[] = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((page) => page.data)
        .map((casting) => ({
          value: casting.uuid,
          label: casting.name,
          avatarUrl: casting.cover_picture_1_1,
        })),
    [data],
  );

  // Seed display data (label/avatar) for the already-selected castings so the
  // chips render correctly even if they are not in the loaded pages yet, then
  // layer the fetched options on top.
  const options: AvatarComboboxOption[] = useMemo(() => {
    const byValue = new Map<string, AvatarComboboxOption>();
    value.forEach((casting) => {
      byValue.set(casting.casting_uuid, {
        value: casting.casting_uuid,
        label: casting.name ?? t("editor-casting:newCasting"),
        avatarUrl: casting.picture,
      });
    });
    searchOptions.forEach((option) => byValue.set(option.value, option));
    return Array.from(byValue.values());
  }, [value, searchOptions, t]);

  const selectedValues = value.map((casting) => casting.casting_uuid);

  // Loading of the initial list or a new search — distinct from paginating,
  // which shows its own bottom loader.
  const isListLoading = isFetching && !isFetchingNextPage;

  const handleChange = (nextValues: string[]): void => {
    onChange(
      nextValues.map((castingUuid, index) => {
        const existing = value.find(
          (casting) => casting.casting_uuid === castingUuid,
        );
        if (existing) {
          return { ...existing, rank: index };
        }
        const option = searchOptions.find(
          (searchOption) => searchOption.value === castingUuid,
        );
        return {
          casting_uuid: castingUuid,
          name: option?.label ?? "",
          picture: option?.avatarUrl ?? null,
          rank: index,
        };
      }),
    );
  };

  return (
    <Block title={t("curators")}>
      <MultiComboboxWithAvatarBase
        emptyLabel={t("curatorsEmpty")}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isListLoading}
        onChange={handleChange}
        onLoadMore={fetchNextPage}
        onSearchChange={setSearch}
        options={options}
        placeholder={t("addCurators")}
        searchPlaceholder={t("curatorsPlaceholder")}
        value={selectedValues}
      />
    </Block>
  );
}
